import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, X } from "lucide-react";
import { createAdminItem, updateAdminItem, uploadAdminImage } from "@/lib/site";

const GECKO_STATUS = ["Tilgængelig", "Planlagt 2027", "Udsolgt"];
const BASE_COLORS = [
  ["red", "Rød"],
  ["yellow", "Gul"],
  ["dark", "Mørk / Sort"],
  ["orange", "Orange"],
  ["lavender", "Oliven / Lavender"],
  ["buckskin", "Bukkeskind"],
];
const TRAIT_OPTIONS = ["Lillywhite", "Frappuccino", "Sable", "Phantom"];
const PATTERN_OPTIONS = [
  "",
  "Patternless",
  "Flame",
  "Harlequin",
  "Extreme",
  "Pinstripe",
  "Quadstripe",
  "Tiger",
  "Brindle",
  "Dalmatiner",
  "Super Dalmatiner",
  "Halloween",
  "Creamsicle",
];
const LINE_OPTIONS = [
  ["", "Ingen"],
  ["cappuccino", "Cappuccino"],
  ["axanthic", "Axanthic"],
  ["tri-color", "Tri-color"],
  ["confetti", "Confetti Dalmatian"],
];
const PRODUCT_CATEGORIES = ["Foder", "Tilskud", "Tilbehør", "Startpakke"];
const TERRARIUM_KINDS = [
  ["ready", "Færdigbygget"],
  ["custom", "Specialbygning"],
];

const field =
  "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-forest";
const label = "font-mono text-[10px] uppercase tracking-[0.15em] text-sage";

function ImageUploader({ images, onChange, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [...images];
      for (const f of files) {
        const res = await uploadAdminImage(f);
        urls.push(res.url);
      }
      onChange(urls);
      toast.success(`${files.length} billede(r) uploadet`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fejlede");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testid}>
      <p className={label}>Billeder</p>
      <div className="mt-2 flex flex-wrap gap-3">
        {images.map((url, i) => (
          <div key={url} className="relative">
            <img src={url} alt="" className="h-20 w-20 border border-white/10 object-cover" />
            <button
              type="button"
              data-testid={`${testid}-remove-${i}`}
              onClick={() => onChange(images.filter((u) => u !== url))}
              className="absolute -right-2 -top-2 rounded-full border border-white/20 bg-ink p-1 text-sage transition-colors hover:text-cream"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          data-testid={`${testid}-add`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex h-20 w-20 items-center justify-center border border-dashed border-white/25 text-sage transition-colors hover:border-forest hover:text-cream disabled:opacity-50"
        >
          {uploading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <ImagePlus className="h-5 w-5" />
          )}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        onChange={pick}
        className="hidden"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}

export function ItemEditor({ collection, item, onClose, onSaved }) {
  const isEdit = Boolean(item);
  const [form, setForm] = useState({
    name: item?.name || "",
    price_dkk: item?.price_dkk ?? "",
    description: item?.description || "",
    inventory_qty: item?.inventory_qty ?? 1,
    status: item?.status || (collection === "terrariums" ? "I webshoppen nu" : "Tilgængelig"),
    weight_kg: item?.weight_kg ?? 0.5,
    category: item?.category || "Tilbehør",
    no_postal: item?.no_postal || false,
    base_color: item?.base_color || "yellow",
    traits: item?.traits || [],
    pattern: item?.pattern || "",
    line: item?.line || "",
    featured: item?.featured || false,
    kind: item?.kind || "ready",
    variants: item?.variants || [],
    images: item?.images || (item?.image ? [item.image] : []),
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const tid = isEdit ? `admin-edit-${item.slug}` : `admin-create-${collection}`;

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Navn mangler");
    if (!Number(form.price_dkk) || Number(form.price_dkk) <= 0)
      return toast.error("Prisen skal være over 0 kr");
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      price_dkk: Number(form.price_dkk),
      description: form.description,
      inventory_qty: Number(form.inventory_qty) || 0,
      images: form.images,
    };
    if (collection === "geckos") {
      Object.assign(payload, {
        status: form.status,
        base_color: form.base_color,
        traits: form.traits,
        pattern: form.pattern || null,
        line: form.line || null,
        featured: form.featured,
      });
    } else if (collection === "products") {
      Object.assign(payload, { category: form.category, weight_kg: Number(form.weight_kg) || 0, no_postal: form.no_postal });
    } else {
      Object.assign(payload, {
        status: form.status,
        kind: form.kind,
        variants: form.variants.filter((v) => v.opt && Number(v.price_dkk) > 0),
      });
    }
    try {
      if (isEdit) {
        await updateAdminItem(collection, item.slug, payload);
        toast.success(`${form.name} opdateret`);
      } else {
        await createAdminItem(collection, payload);
        toast.success(`${form.name} oprettet`);
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke gemme");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-ink/80 backdrop-blur-sm" data-testid={`${tid}-modal`}>
      <div className="mx-auto my-16 w-full max-w-2xl border border-white/10 bg-moss p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl tracking-tight text-cream">
            {isEdit ? `Rediger: ${item.name}` : "Ny vare"}
          </h2>
          <button
            type="button"
            data-testid={`${tid}-close`}
            onClick={onClose}
            className="text-sage transition-colors hover:text-cream"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={save} className="mt-8 space-y-5" data-testid={`${tid}-form`}>
          <div>
            <p className={label}>Navn</p>
            <input
              data-testid={`${tid}-name`}
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className={`${field} mt-2`}
              placeholder={collection === "geckos" ? "Fx Lillywhite Harlequin" : "Fx Pangea Complete Diet"}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={label}>Pris (kr)</p>
              <input
                data-testid={`${tid}-price`}
                type="number"
                min="1"
                value={form.price_dkk}
                onChange={(e) => set("price_dkk", e.target.value)}
                className={`${field} mt-2`}
              />
            </div>
            <div>
              <p className={label}>Lager</p>
              <input
                data-testid={`${tid}-stock`}
                type="number"
                min="0"
                value={form.inventory_qty}
                onChange={(e) => set("inventory_qty", e.target.value)}
                className={`${field} mt-2`}
              />
            </div>
          </div>

          <div>
            <p className={label}>Beskrivelse</p>
            <textarea
              data-testid={`${tid}-description`}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={5}
              className={`${field} mt-2 resize-y`}
              placeholder="Beskriv varen på dansk…"
            />
          </div>

          <ImageUploader images={form.images} onChange={(v) => set("images", v)} testid={`${tid}-images`} />

          {collection === "geckos" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className={label}>Status</p>
                  <select
                    data-testid={`${tid}-status`}
                    value={form.status}
                    onChange={(e) => set("status", e.target.value)}
                    className={`${field} mt-2`}
                  >
                    {GECKO_STATUS.map((s) => (
                      <option key={s} value={s} className="bg-ink">{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className={label}>Grundfarve</p>
                  <select
                    data-testid={`${tid}-base-color`}
                    value={form.base_color}
                    onChange={(e) => set("base_color", e.target.value)}
                    className={`${field} mt-2`}
                  >
                    {BASE_COLORS.map(([v, l]) => (
                      <option key={v} value={v} className="bg-ink">{l}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <p className={label}>Egenskaber</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {TRAIT_OPTIONS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`${tid}-trait-${t.toLowerCase()}`}
                      onClick={() =>
                        set(
                          "traits",
                          form.traits.includes(t)
                            ? form.traits.filter((x) => x !== t)
                            : [...form.traits, t]
                        )
                      }
                      className={`rounded-full border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                        form.traits.includes(t)
                          ? "border-forest bg-forest text-cream"
                          : "border-white/15 text-sage hover:border-white/40"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className={label}>Mønster</p>
                  <select
                    data-testid={`${tid}-pattern`}
                    value={form.pattern}
                    onChange={(e) => set("pattern", e.target.value)}
                    className={`${field} mt-2`}
                  >
                    {PATTERN_OPTIONS.map((p) => (
                      <option key={p} value={p} className="bg-ink">{p || "Intet"}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className={label}>GEKKOFUEL-linje</p>
                  <select
                    data-testid={`${tid}-line`}
                    value={form.line}
                    onChange={(e) => set("line", e.target.value)}
                    className={`${field} mt-2`}
                  >
                    {LINE_OPTIONS.map(([v, l]) => (
                      <option key={v} value={v} className="bg-ink">{l}</option>
                    ))}
                    {form.line && !LINE_OPTIONS.some(([v]) => v === form.line) && (
                      <option value={form.line} className="bg-ink">{form.line}</option>
                    )}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-3 text-sm text-sage">
                <input
                  type="checkbox"
                  data-testid={`${tid}-featured`}
                  checked={form.featured}
                  onChange={(e) => set("featured", e.target.checked)}
                  className="h-4 w-4 accent-forest"
                />
                Fremhæv på forsiden
              </label>
            </>
          )}

          {collection === "products" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className={label}>Kategori</p>
                <select
                  data-testid={`${tid}-category`}
                  value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                  className={`${field} mt-2`}
                >
                  {PRODUCT_CATEGORIES.map((c) => (
                    <option key={c} value={c} className="bg-ink">{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className={label}>Vægt (kg)</p>
                <input
                  data-testid={`${tid}-weight`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.weight_kg}
                  onChange={(e) => set("weight_kg", e.target.value)}
                  className={`${field} mt-2`}
                />
              </div>
            </div>
          )}

          {collection === "products" && (
            <label
              data-testid={`${tid}-no-postal`}
              className="flex cursor-pointer items-center gap-3 border border-white/10 p-4 text-sm text-cream"
            >
              <input
                type="checkbox"
                checked={form.no_postal}
                onChange={(e) => set("no_postal", e.target.checked)}
                className="h-4 w-4 accent-[#4A6B41]"
              />
              Kan ikke sendes med post — kun afhentning i Billund eller personlig levering
            </label>
          )}

          {collection === "terrariums" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className={label}>Status</p>
                  <input
                    data-testid={`${tid}-status`}
                    value={form.status}
                    onChange={(e) => set("status", e.target.value)}
                    className={`${field} mt-2`}
                  />
                </div>
                <div>
                  <p className={label}>Type</p>
                  <select
                    data-testid={`${tid}-kind`}
                    value={form.kind}
                    onChange={(e) => set("kind", e.target.value)}
                    className={`${field} mt-2`}
                  >
                    {TERRARIUM_KINDS.map(([v, l]) => (
                      <option key={v} value={v} className="bg-ink">{l}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <p className={label}>Varianter (valgfri)</p>
                <div className="mt-2 space-y-2">
                  {form.variants.map((v, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        data-testid={`${tid}-variant-name-${i}`}
                        value={v.opt}
                        onChange={(e) =>
                          set("variants", form.variants.map((x, j) => (j === i ? { ...x, opt: e.target.value } : x)))
                        }
                        placeholder="Fx 45x45x60 cm"
                        className={`${field} flex-1`}
                      />
                      <input
                        data-testid={`${tid}-variant-price-${i}`}
                        type="number"
                        min="1"
                        value={v.price_dkk}
                        onChange={(e) =>
                          set("variants", form.variants.map((x, j) => (j === i ? { ...x, price_dkk: Number(e.target.value) } : x)))
                        }
                        placeholder="Pris"
                        className={`${field} w-28`}
                      />
                      <button
                        type="button"
                        data-testid={`${tid}-variant-remove-${i}`}
                        onClick={() => set("variants", form.variants.filter((_, j) => j !== i))}
                        className="text-sage transition-colors hover:text-cream"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    data-testid={`${tid}-variant-add`}
                    onClick={() => set("variants", [...form.variants, { opt: "", price_dkk: "" }])}
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-pale transition-colors hover:text-cream"
                  >
                    + Tilføj variant
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              data-testid={`${tid}-save`}
              disabled={saving}
              className="flex items-center gap-3 rounded-full bg-forest px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pale hover:text-ink disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Gem ændringer" : "Opret vare"}
            </button>
            <button
              type="button"
              data-testid={`${tid}-cancel`}
              onClick={onClose}
              className="rounded-full border border-white/15 px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] text-sage transition-colors hover:border-white/40 hover:text-cream"
            >
              Annuller
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
